import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { FC, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ImmersivePostItemAtoms } from '@discover/body/immersive-post/immersive-post-item/atoms';
import { IVideoArticleDetail } from 'shared/models';
import { IArticleRelatedPost } from 'shared/atoms';
import { VideoEndRecommend } from '~/components/video-end-recommend';
import PlayFill from '@lemon8/design/lib/icons/icon_assets/play_fill.svg';
import SpeakerMutedFillIcon from '@lemon8/design/lib/icons/icon_assets/speaker_x_mark_fill.svg';
import SpeakerWaveFillIcon from '@lemon8/design/lib/icons/icon_assets/speaker_wave_2_fill.svg';
import SimpleVideo, { SimpleVideoForwardProps, VideoStatus } from '~/components/simple-video';
import { IVideoEntry, videoPlayController } from '~/components/video-controller';
import { ClickPosition } from '~/utils/click-position';
import { useAtomValue, useSetAtom } from 'jotai';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { useTranslation } from 'react-i18next';
import { $SimpleCTAModalShow } from '~/models/portal/simple-cta-modal';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';

import './style.scss';

interface IArticleVideo {
  articleRelatedPosts: IArticleRelatedPost;
  loadArticleRelated?: () => void;
}

export const ArticleVideo: FC<IArticleVideo> = (props) => {
  const { articleRelatedPosts, loadArticleRelated } = props;
  const { t } = useTranslation();
  const article = useScopedAtomValue(ImmersivePostItemAtoms.$CurrentArticle) as IVideoArticleDetail;
  const isMobileUser = useAtomValue($IsMobileUser);
  const setSimpleCTAModal = useSetAtom($SimpleCTAModalShow);
  const activeToArticle = useActiveToArticle(article);

  const [showPlay, setShowPlay] = useState(false);
  const [muted, setMuted] = useState(true);
  const [endRecommendShow, setEndRecommendShow] = useState(false);

  const simpleVideoRef = useRef<SimpleVideoForwardProps>(null);

  const isFirstPlay = useRef(false);
  useEffect(() => {
    if (!simpleVideoRef.current) {
      return;
    }

    const simpleVideo = simpleVideoRef.current;

    const handlePlayEvent = (): void => {
      if (isFirstPlay.current) {
        window.Tea('wap_video_play');
      }
      isFirstPlay.current = false;
      setShowPlay(false);
      setEndRecommendShow(false);
    };

    const handlePlayingEvent = (): void => {
      setShowPlay(false);
      setEndRecommendShow(false);
    };

    const handlePauseEvent = (): void => {
      setShowPlay(true);
    };

    const handleEndEvent = (): void => {
      setEndRecommendShow(true);
    };

    const handleVolumeChange = () => {
      const video = simpleVideo?.video;
      if (!video) {
        return;
      }

      setMuted(video.muted);
    };

    simpleVideo.addEventListener('play', handlePlayEvent);
    simpleVideo.addEventListener('playing', handlePlayingEvent);
    simpleVideo.addEventListener('pause', handlePauseEvent);
    simpleVideo.addEventListener('ended', handleEndEvent);
    simpleVideo.addEventListener('volumechange', handleVolumeChange);

    return () => {
      simpleVideo.removeEventListener('play', handlePlayEvent);
      simpleVideo.removeEventListener('playing', handlePlayingEvent);
      simpleVideo.removeEventListener('pause', handlePauseEvent);
      simpleVideo.removeEventListener('ended', handleEndEvent);
      simpleVideo.removeEventListener('volumechange', handleVolumeChange);
    };
  }, []);

  const videoClicked = useRef(false);
  const handleVideoClick = useCallback(() => {
    window.Tea('wap_click', {
      current_position: ClickPosition.main_video_pause_manual,
      group_id: article.groupId,
    });
    simpleVideoRef.current?.pause();

    if (!videoClicked.current && isMobileUser) {
      setSimpleCTAModal({
        body: t('seo_stopVideo_modal_desc'),
        currentPosition: ClickPosition.main_video_pause_manual_modal,
        handleClickConfirm: () => {
          activeToArticle(ClickPosition.main_video_pause_manual_modal);
        },
      });

      videoClicked.current = true;
    }
  }, [setSimpleCTAModal, activeToArticle]);

  const handlePlayClick = useCallback((event: SyntheticEvent) => {
    event.preventDefault();
    simpleVideoRef.current?.play();
  }, []);

  const handleVolumeClicked = useCallback(() => {
    if (simpleVideoRef.current) {
      if (simpleVideoRef.current.muted) {
        simpleVideoRef.current.unmute();
      } else {
        simpleVideoRef.current.mute();
      }
    }
  }, []);

  const videoReplayHandler = useCallback(() => {
    if (simpleVideoRef.current) {
      simpleVideoRef.current.seek(0);
      simpleVideoRef.current.play();
    }
  }, [simpleVideoRef]);

  useEffect(() => {
    const simpleVideo = simpleVideoRef.current;
    if (!simpleVideo) {
      return;
    }

    const entry: IVideoEntry = {
      element: simpleVideo.video,
      pause: (position: ClickPosition) => {
        simpleVideo.pause();
        window.Tea('wap_click', {
          current_position: position,
          group_id: article.groupId,
        });
      },
      play: () => {
        simpleVideo.play();
      },
    };

    videoPlayController.register(entry);

    return () => {
      videoPlayController.unregister(entry);
    };
  }, []);

  // progress > 0.5, fetch related post
  useEffect(() => {
    if (!simpleVideoRef.current) {
      return;
    }

    const simpleVideo = simpleVideoRef.current;

    simpleVideo.addEventListener('timeupdate', (e) => {
      const video = e.currentTarget as HTMLVideoElement;
      if (video.currentTime / video.duration > 0.5) {
        loadArticleRelated?.();
      }
    });
  }, [simpleVideoRef, loadArticleRelated]);

  return (
    <div className="article-video">
      <SimpleVideo
        ref={simpleVideoRef}
        videoSrc={article.video.url}
        posterSrc={article.largeImage.url}
        onClick={handleVideoClick}
        muteStatus={article.video.muteStatus}
      />

      {showPlay && (
        <div className="article-video-play-icon" onClick={handlePlayClick}>
          <PlayFill />
        </div>
      )}

      <div className="article-video-volume-icon" onClick={handleVolumeClicked}>
        {muted ? <SpeakerMutedFillIcon /> : <SpeakerWaveFillIcon />}
      </div>

      <VideoEndRecommend
        show={endRecommendShow}
        article={article}
        relatedPost={articleRelatedPosts}
        onClickReplay={videoReplayHandler}
      />
    </div>
  );
};
